import moment from 'moment';
import { useQuery } from '@apollo/client';
import { useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Table from 'react-bootstrap/Table';
import { USER_GIFT_STATUS_ADMIN } from '../../../graphql';

export type UserGiftStatusInfo = {
  isFriend?: boolean | null;
  isSystem?: boolean | null;
  sender: {
    id: string;
    displayName?: string | null;
  };
  gift: {
    id: string;
    created?: string | null;
    claimed?: string | null;
    bundleName?: string | null;
    message?: string | null;
    rewards?: Array<{
      __typename?: string | null;
      id?: string | null;
      name?: string | null;
      type?: string | null;
      badgeType?: string | null;
      rewardType?: string | null;
      posterType?: string | null;
      amount?: number | null;
      boostType?: string | null;
      unlimitedBoostType?: string | null;
      category?: {
        id?: string | null;
        name?: string | null;
      } | null;
      contentPack?: {
        id?: string | null;
        name?: string | null;
      } | null;
    }> | null;
  };
};

type Props = {
  userId: string;
};

const formatClaimedStatus = (claimed: UserGiftStatusInfo['gift']['claimed']) => {
  if (!claimed) {
    return 'Pending';
  }
  return `Claimed at ${moment(claimed).format('YYYY-MM-DD HH:mm:ss')}`;
};

const formatSentAt = (created: UserGiftStatusInfo['gift']['created']) => {
  if (!created) {
    return '-';
  }
  return moment(created).format('YYYY-MM-DD HH:mm:ss');
};

const getSenderLabel = (giftInfo: UserGiftStatusInfo) => {
  if (giftInfo.isSystem) {
    return 'System';
  }
  return giftInfo.sender.displayName || giftInfo.sender.id;
};

const formatRewards = (rewards: UserGiftStatusInfo['gift']['rewards']) => {
  if (!rewards?.length) {
    return '-';
  }

  return rewards
    .map((reward) => {
      const amountSuffix = reward.amount ? ` x${reward.amount}` : '';

      switch (reward.__typename) {
        case 'CurrencyBoost':
          return `${reward.boostType || 'BOOST'}${amountSuffix}`;
        case 'CurrencyUnlimitedBoost':
          return `${reward.unlimitedBoostType || 'UNLIMITED_BOOST'}${amountSuffix}`;
        case 'CurrencyCategoryFragment':
          return `${reward.category?.name || 'CATEGORY_FRAGMENT'}${amountSuffix}`;
        case 'CurrencyCategoryRandom':
          return `${reward.category?.name || 'CATEGORY_RANDOM'}${amountSuffix}`;
        case 'ContentPackReward':
          return reward.contentPack?.name || reward.rewardType || reward.__typename || 'CONTENT_PACK';
        case 'AppSkin':
        case 'Badge':
        case 'Emoji':
        case 'EmojiPack':
        case 'ProfileFrame':
        case 'Sticker':
          return reward.name || reward.badgeType || reward.type || reward.__typename || reward.id || 'ITEM';
        case 'Poster':
          return reward.name || reward.posterType || reward.__typename || reward.id || 'ITEM';
        default:
          return `${reward.__typename || reward.type || reward.id || 'REWARD'}${amountSuffix}`;
      }
    })
    .join(', ');
};

type UserGiftStatusAdminQuery = {
  userGiftStatusAdmin?: UserGiftStatusInfo[];
};

type UserGiftStatusAdminQueryVariables = {
  userId: string;
};

const UserGiftStatusDisplay = ({ userId }: Props) => {
  const [expanded, setExpanded] = useState(false);
  const { data, loading } = useQuery<UserGiftStatusAdminQuery, UserGiftStatusAdminQueryVariables>(
    USER_GIFT_STATUS_ADMIN,
    {
      variables: { userId },
      skip: !userId,
      fetchPolicy: 'no-cache',
    }
  );
  const gifts = data?.userGiftStatusAdmin ?? [];
  const pendingCount = gifts.filter(({ gift }) => !gift.claimed).length;
  const claimedCount = gifts.length - pendingCount;

  return (
    <Card className="mb-1">
      <Card.Body>
        <Accordion>
          <Accordion.Toggle as={Card.Title} variant="link" eventKey="0" onClick={() => setExpanded(!expanded)}>
            <div style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
              Gift Status
              <Button variant="link">
                {!expanded && <i className="fas fa-chevron-down" />}
                {expanded && <i className="fas fa-chevron-up" />}
              </Button>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            <>
              {loading && <div className="mb-3">Loading...</div>}
              <div className="mb-3">
                <strong>Total:</strong> {gifts.length} {' | '}
                <strong>Pending:</strong> {pendingCount} {' | '}
                <strong>Claimed:</strong> {claimedCount}
              </div>
              {gifts.length === 0 ? (
                <i>No gifts found for this user.</i>
              ) : (
                <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>Gift ID</th>
                        <th>Sent At</th>
                        <th>Sender</th>
                        <th>Status</th>
                        <th>Bundle</th>
                        <th>Rewards</th>
                        <th>Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gifts.map((giftInfo) => (
                        <tr key={giftInfo.gift.id}>
                          <td>{giftInfo.gift.id}</td>
                          <td>{formatSentAt(giftInfo.gift.created)}</td>
                          <td>{getSenderLabel(giftInfo)}</td>
                          <td>{formatClaimedStatus(giftInfo.gift.claimed)}</td>
                          <td>{giftInfo.gift.bundleName || '-'}</td>
                          <td>{formatRewards(giftInfo.gift.rewards)}</td>
                          <td>{giftInfo.gift.message || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>
    </Card>
  );
};

export default UserGiftStatusDisplay;
